<template>
  <vue-final-modal
    classes="flex justify-center items-center"
    v-model="modalVisible"
  >
    <div class="bg-white rounded-lg shadow-lg w-80 max-w-sm mx-4">
      <!-- Header -->
      <div class="px-4 py-3 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900">{{ modalTitle }}</h3>
        <label class="flex items-center cursor-pointer mt-2">
          <input 
            type="checkbox" 
            :checked="allSelected"
            @change="toggleSelectAll"
            class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span class="ml-2 text-sm font-medium text-gray-700">Select All</span>
        </label>
      </div>

      <!-- Version List -->
      <div class="max-h-64 overflow-y-auto">
        <div v-if="versions.length === 0" class="px-4 py-6 text-center text-gray-500">
          No versions available
        </div>
        <div v-else>
          <div 
            v-for="version in versions" 
            :key="version.version"
            class="px-4 py-2 border-b border-gray-100 hover:bg-gray-50"
          >
            <label class="flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                :value="getVersionValue(version)"
                v-model="selectedVersions"
                class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div class="ml-3 flex-1">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-gray-900" >{{ getVersionDisplay(version) }}</span>
                  <div class="flex space-x-2">
                    <span 
                      v-if="version.is_current" 
                      class="text-xs text-gray-500"
                    >
                      current
                    </span>
                    <span 
                      v-if="version.is_published" 
                      class="text-xs text-gray-500"
                    >
                      published
                    </span>
                  </div>
                </div>
              </div>
            </label>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-4 py-3 border-t border-gray-200 flex justify-end space-x-2">
        <button 
          @click="handleCancel"
          class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button 
          @click="handleConfirm"
          :disabled="selectedVersions.length === 0"
          class="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 border border-transparent rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:hover:bg-gray-400"
        >
          {{ buttonText }}
        </button>
      </div>
    </div>
  </vue-final-modal>
</template>

<script>
export default {
  name: 'VersionSelectionModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    versions: {
      type: Array,
      default: () => []
    },
    modalTitle: {
      type: String,
      default: 'Select Versions to Copy'
    },
    actionText: {
      type: String,
      default: 'Copy'
    }
  },
  data() {
    return {
      selectedVersions: []
    }
  },
  computed: {
    modalVisible: {
      get() {
        return this.isVisible
      },
      set(value) {
        this.$emit('update:isVisible', value)
      }
    },
    allSelected() {
      if (this.versions.length === 0) return false
      return this.selectedVersions.length === this.versions.length && this.selectedVersions.length > 0
    },
    buttonText() {
      const count = this.selectedVersions.length
      if (count === 0) {
        return `${this.actionText} Versions`
      }
      return `${this.actionText} ${count} Version${count !== 1 ? 's' : ''}`
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.selectedVersions = []
      }
    }
  },
  methods: {
    getVersionValue(version) {
      // Handle different API response structures
      if (Array.isArray(version)) {
        return version[0] // Take first element if it's an array
      }
      return version.version || version._id || version
    },
    getVersionDisplay(version) {
      // Handle different API response structures for display
      if (Array.isArray(version)) {
        return version.join(', ') // Display array as comma-separated
      }
      return version.version || version._id || version
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedVersions = []
      } else {
        this.selectedVersions = this.versions.map(v => this.getVersionValue(v))
      }
    },
    handleCancel() {
      this.$emit('cancel')
      // Hide modal immediately
      this.$emit('update:isVisible', false)
    },
    handleConfirm() {
      if (this.selectedVersions.length > 0) {
        this.$emit('confirm', this.selectedVersions)
      }
    }
  }
}
</script>

<style scoped>
/* Custom scrollbar for version list */
.max-h-64::-webkit-scrollbar {
  width: 4px;
}

.max-h-64::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.max-h-64::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.max-h-64::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Enhanced disabled button styling */
button:disabled {
  background-color: #9ca3af !important;
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

button:disabled:hover {
  background-color: #9ca3af !important;
}
</style>
